// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Conduit — the fittings, and the typed half-edge that is the whole game.
//
// A cell presents FOUR SIDES, [N, E, S, W], each one of BLANK / CULVERT / FILAMENT. That single
// data structure is what makes this game not a shape-placement game: a placement is legal or illegal
// by TOPOLOGY, not by whether the square happens to be free. Every neighbouring pair of sides must
// agree exactly — including blank against blank, which is what stops a fitting dead-ending into the
// side of work you have already drawn.
//
// ACHIRALITY IS AN INVARIANT, NOT A CONVENIENCE. Every fitting's mirror image is one of its own
// rotations, which is the ONLY thing licensing the removal of a reflect control from a phone UI that
// has no room for one. tests/pieces.test.ts asserts it over all 8 x 2 mirrors rather than trusting
// this comment, because the day somebody adds a chiral fitting the UI silently loses half its
// orientations and nothing else complains.

/** A side's gauge. 0 is not "no gauge" — it is a real, matchable state meaning "a solid wall". */
export const BLANK = 0;
export const CULVERT = 1;
export const FILAMENT = 2;
export type Gauge = typeof BLANK | typeof CULVERT | typeof FILAMENT;

/** Sides are always indexed in this order, everywhere, so rotation is a rotate of the tuple. */
export const N = 0;
export const E = 1;
export const S = 2;
export const W = 3;
export type Dir = 0 | 1 | 2 | 3;

export const DIRS: readonly Dir[] = [N, E, S, W];
/** Step per direction, as (dx, dy) with y increasing downward. */
export const DX: readonly number[] = [0, 1, 0, -1];
export const DY: readonly number[] = [-1, 0, 1, 0];
/** The side of the neighbour that faces you. */
export const opp = (d: Dir): Dir => ((d + 2) % 4) as Dir;

export type Sides = readonly [Gauge, Gauge, Gauge, Gauge];

export type PieceId = 'cs' | 'ce' | 'ct' | 'fs' | 'fe' | 'ft' | 'cp' | 'bp';

export interface Piece {
  readonly id: PieceId;
  readonly name: string;
  /** Rotation 0. Every other rotation is derived, never written out by hand. */
  readonly base: Sides;
  /** How many DISTINCT rotations this fitting has (1, 2 or 4). */
  readonly rots: number;
  /**
   * Deal weight out of 100. Elbows are the commonest because they are what lets a network turn;
   * a deal of straights only can paint you into a corner you cannot leave.
   */
  readonly weight: number;
  /**
   * Whether the two gauges inside this cell are CONNECTED to each other.
   * Only the coupler joins them. The bypass carries both straight through and joins neither.
   */
  readonly couples: boolean;
}

/**
 * Eight fittings.
 *
 * The coupler is deliberately scarce (weight 10, so ~3.2 in a 32-mark run): it is the ONLY way a
 * culvert vent and a filament vent ever end up in the same component, which is what makes a plate
 * holding all twelve vents a real achievement rather than a matter of playing long enough.
 *
 * A 4-way cross is deliberately ABSENT. It has one rotation, it is rare, and every time it lands it
 * manufactures open ends faster than the rest of the deal can close them.
 */
export const PIECES: Record<PieceId, Piece> = {
  cs: { id: 'cs', name: 'Culvert straight', base: [CULVERT, BLANK, CULVERT, BLANK], rots: 2, weight: 15, couples: false },
  ce: { id: 'ce', name: 'Culvert elbow', base: [CULVERT, CULVERT, BLANK, BLANK], rots: 4, weight: 17, couples: false },
  ct: { id: 'ct', name: 'Culvert tee', base: [CULVERT, CULVERT, CULVERT, BLANK], rots: 4, weight: 9, couples: false },
  fs: { id: 'fs', name: 'Filament straight', base: [FILAMENT, BLANK, FILAMENT, BLANK], rots: 2, weight: 15, couples: false },
  fe: { id: 'fe', name: 'Filament elbow', base: [FILAMENT, FILAMENT, BLANK, BLANK], rots: 4, weight: 17, couples: false },
  ft: { id: 'ft', name: 'Filament tee', base: [FILAMENT, FILAMENT, FILAMENT, BLANK], rots: 4, weight: 9, couples: false },
  cp: { id: 'cp', name: 'Coupler', base: [CULVERT, BLANK, FILAMENT, BLANK], rots: 4, weight: 10, couples: true },
  bp: { id: 'bp', name: 'Bypass', base: [CULVERT, FILAMENT, CULVERT, FILAMENT], rots: 2, weight: 8, couples: false },
};

export const PIECE_IDS: readonly PieceId[] = ['cs', 'ce', 'ct', 'fs', 'fe', 'ft', 'cp', 'bp'];

/** Rotate a side tuple clockwise `r` quarter-turns. N becomes E, so index d moves to (d + r) % 4. */
export function rotate(s: Sides, r: number): Sides {
  const k = ((r % 4) + 4) % 4;
  const out: Gauge[] = [BLANK, BLANK, BLANK, BLANK];
  for (let d = 0; d < 4; d++) out[(d + k) % 4] = s[d];
  return out as unknown as Sides;
}

/** Mirror a side tuple left-to-right: E and W swap, N and S stay. */
export function mirror(s: Sides): Sides {
  return [s[N], s[W], s[S], s[E]] as unknown as Sides;
}

const key = (s: Sides): string => s.join('');

/**
 * Every distinct orientation of a fitting, in a stable order, deduped.
 *
 * Deduping matters for more than tidiness: the rotate button shows "2 of 4" and cycling through a
 * repeat would read as a broken control.
 */
export function formsOf(id: PieceId): readonly Sides[] {
  const p = PIECES[id];
  const seen = new Set<string>();
  const out: Sides[] = [];
  for (let r = 0; r < 4; r++) {
    const s = rotate(p.base, r);
    const k = key(s);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(s);
  }
  return out;
}

/** Precomputed once — the hot loops in the bots and the sim call this thousands of times a run. */
export const FORMS: Record<PieceId, readonly Sides[]> = Object.fromEntries(
  PIECE_IDS.map((id) => [id, formsOf(id)]),
) as Record<PieceId, readonly Sides[]>;

/**
 * Whether a fitting's mirror is one of its own rotations.
 *
 * If this is false for ANY fitting, the UI must grow a reflect control — see the file header. It is
 * asserted in tests/pieces.test.ts, not merely available.
 */
export function isAchiral(id: PieceId): boolean {
  const mk = key(mirror(PIECES[id].base));
  return FORMS[id].some((f) => key(f) === mk);
}

/** Whether two gauges may sit against each other across a boundary. Exact equality, always. */
export const matches = (a: Gauge, b: Gauge): boolean => a === b;

/** A side that carries something. Blank sides are walls and are never open ends. */
export const live = (g: Gauge): boolean => g !== BLANK;

export const gaugeName = (g: Gauge): string =>
  g === CULVERT ? 'culvert' : g === FILAMENT ? 'filament' : 'blank';

export const DIR_NAME: readonly string[] = ['north', 'east', 'south', 'west'];
